import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpDelete, httpGet, httpPost } from "../../services/httpService";
import {
  getScheduleEventsUrl,
  getScheduleEventsByPharmaciesUrl,
  getResourcesUrl,
  addScheduleEventUrl,
  deleteScheduleEventsUrl,
  addBlockOutUrl,
  updateBlockOutUrl,
  deleteBlockOutUrl,
  getUsrAvailibilityUrl,
  getUsersAvailabilityByPriorityUrl,
  getScheduleEventsByCompanyUrl,
  saveGoogleEventsUrl,
  getGoogleEventsUrl,
  getAllUserWeekAvailabilityUrl,
  addAndUpdateTaskEventUrl,
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";

const initialState = {
  isEventsLoading: false,
  isEventCalendarLoading: false,
  scheduleEvents: [],
  resources: [],
  userAvailibility: [],
  myAppointments: [],
  isMyAppointmentsLoading: false,
  googleSyncEvents: [],
  showBookingSlots: false,
  bookingSlotsMode: "",
  setShowBacktoSlotsButton: false,
  scheduleEventsBySelectedPharmacies: [],
  isScheduleEventsByPharmaciesLoading: false,
  allUserWeekAvailability: [],
};

export const getScheduleEventsData = createAsyncThunk(
  "scheduleEvents/getScheduleEventsData",
  async (pharmacyId, thunkAPI) => {
    const urlToGet = getScheduleEventsUrl(pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getResourcesData = createAsyncThunk(
  "scheduleEvents/getResourcesData",
  async ({ companyID, pharmacyId }) => {
    const urlToGet = getResourcesUrl(companyID, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const addScheduleEventData = createAsyncThunk(
  "scheduleEvents/addScheduleEventData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(addScheduleEventUrl(pharmacyId), data);
    return respData;
  },
);
export const addBlockOutData = createAsyncThunk(
  "scheduleEvents/addBlockOutData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(addBlockOutUrl(pharmacyId), data);
    return respData;
  },
);
export const updateBlockOutData = createAsyncThunk(
  "scheduleEvents/updateBlockOutData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(updateBlockOutUrl(pharmacyId), data);
    return respData;
  },
);
export const deleteScheduleEventsData = createAsyncThunk(
  "scheduleEvents/deleteScheduleEventsData",
  async (params, thunkAPI) => {
    const { eventID, currentPharmacyId } = params;
    const urlToGet = deleteScheduleEventsUrl(eventID, currentPharmacyId);
    const respData = await httpDelete(urlToGet);
    return respData;
  },
);
export const deleteBlockOutData = createAsyncThunk(
  "scheduleEvents/deleteBlockOutData",
  async (params, thunkAPI) => {
    const { eventID, currentPharmacyId } = params;
    const urlToGet = deleteBlockOutUrl(eventID, currentPharmacyId);
    const respData = await httpDelete(urlToGet);
    return respData;
  },
);
export const getUsrAvailibilityData = createAsyncThunk(
  "scheduleEvents/getUsrAvailibilityData",
  async ({ userEmail, serviceID, pharmacyId }) => {
    const urlToGet = getUsrAvailibilityUrl(userEmail, serviceID, pharmacyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getUsersAvailabilityByPriorityData = createAsyncThunk(
  "scheduleEvents/getUsersAvailabilityByPriorityData",
  async ({ companyID, serviceID, pharmacyID }) => {
    const urlToGet = getUsersAvailabilityByPriorityUrl(companyID, serviceID, pharmacyID);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getScheduleEventsByPharmaciesData = createAsyncThunk(
  "scheduleEvents/getScheduleEventsByPharmaciesData",
  async (pharmacyIds, thunkAPI) => {
    const urlToGet = getScheduleEventsByPharmaciesUrl();
    const payload = Array.isArray(pharmacyIds)
      ? pharmacyIds.filter((id) => Boolean(id))
      : [];
    const respData = await httpPost(urlToGet, payload);
    return respData;
  },
);

export const getMyAppointmentsData = createAsyncThunk(
  "scheduleEvents/getMyAppointmentsData",
  async () => {
    const urlToGet = getScheduleEventsByCompanyUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const saveGoogleEventsData = createAsyncThunk(
  "scheduleEvents/saveGoogleEventsData",
  async (mappedEvents, thunkAPI) => {
    const urlToPost = saveGoogleEventsUrl();
    const respData = await httpPost(urlToPost, mappedEvents);
    return respData;
  },
);

export const getGoogleEventsData = createAsyncThunk(
  "scheduleEvents/getGoogleEventsData",
  async () => {
    const urlToGet = getGoogleEventsUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getAllUserWeekAvailabilityData = createAsyncThunk(
  "scheduleEvents/getAllUserWeekAvailabilityData",
  async () => {
    const urlToGet = getAllUserWeekAvailabilityUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const addAndUpdateTaskEventData = createAsyncThunk(
  "scheduleEvents/addAndUpdateTaskEventData",
  async (params, thunkAPI) => {
    const { data, pharmacyId } = params || {};
    const respData = await httpPost(addAndUpdateTaskEventUrl(pharmacyId), data);
    return respData;
  },
);

const scheduleEventSlice = createSlice({
  name: "scheduleEvent",
  initialState,
  reducers: {
    setIsEventsLoading: (state, action) => {
      state.isEventsLoading = action?.payload || false;
    },
    setIsEventCalendarLoading: (state, action) => {
      state.isEventCalendarLoading = action?.payload || false;
    },
    setScheduleEventsList: (state, action) => {
      state.scheduleEvents = Array.isArray(action?.payload)
        ? action.payload
        : [];
      // ensure we never show skeleton due to this update
      state.isEventsLoading = false;
    },
    setMyAppointmentList: (state, action) => {
      state.myAppointments = Array.isArray(action?.payload)
        ? action.payload
        : [];
      // ensure we never show skeleton due to this update
      state.isMyAppointmentsLoading = false;
    },
    setShowBookingSlotsData: (state, action) => {
      state.showBookingSlots = action?.payload || false;
    },
    setBookingSlotsModeData: (state, action) => {
      state.bookingSlotsMode = action?.payload || "";
    },
    setShowBacktoSlotsButtonData: (state, action) => {
      state.showBacktoSlotsButton = action?.payload || false;
    },
    setMyAppointments: (state, action) => {
      state.myAppointments = Array.isArray(action?.payload)
        ? action.payload
        : [];
    },
    setGoogleSyncEvents: (state, action) => {
      state.googleSyncEvents = Array.isArray(action?.payload)
        ? action.payload
        : [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScheduleEventsData.pending, (state) => {
        state.isEventsLoading = true;
        state.scheduleEvents = [];
      })
      .addCase(getScheduleEventsData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
        state.scheduleEvents = action?.payload;
      })
      .addCase(getScheduleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
        state.scheduleEvents = [];
      })
      .addCase(getResourcesData.pending, (state) => {
        state.isEventsLoading = true;
        state.resources = [];
      })
      .addCase(getResourcesData.fulfilled, (state, action) => {
        state.isEventsLoading = false;
        state.resources = action?.payload;
      })
      .addCase(getResourcesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventsLoading = false;
        state.resources = [];
      })
      .addCase(addScheduleEventData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(addScheduleEventData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(addScheduleEventData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteScheduleEventsData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(deleteScheduleEventsData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteScheduleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(addBlockOutData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(addBlockOutData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(addBlockOutData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteBlockOutData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(deleteBlockOutData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(deleteBlockOutData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(updateBlockOutData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(updateBlockOutData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(updateBlockOutData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(getUsrAvailibilityData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(getUsrAvailibilityData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
        state.userAvailibility = action?.payload;
      })
      .addCase(getUsrAvailibilityData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
        state.userAvailibility = [];
      })
      .addCase(getUsersAvailabilityByPriorityData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(getUsersAvailabilityByPriorityData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
        const payload = action?.payload;
        state.userAvailibility = Array.isArray(payload?.response)
          ? payload.response
          : Array.isArray(payload)
          ? payload
          : [];
      })
      .addCase(getUsersAvailabilityByPriorityData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
        state.userAvailibility = [];
      })
      .addCase(getScheduleEventsByPharmaciesData.pending, (state) => {
        state.isScheduleEventsByPharmaciesLoading = true;
      })
      .addCase(getScheduleEventsByPharmaciesData.fulfilled, (state, action) => {
        state.isScheduleEventsByPharmaciesLoading = false;
        state.scheduleEventsBySelectedPharmacies = action?.payload || [];
      })
      .addCase(getScheduleEventsByPharmaciesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isScheduleEventsByPharmaciesLoading = false;
        state.scheduleEventsBySelectedPharmacies = [];
      })
      .addCase(getMyAppointmentsData.pending, (state) => {
        state.isMyAppointmentsLoading = true;
        state.myAppointments = [];
      })
      .addCase(getMyAppointmentsData.fulfilled, (state, action) => {
        state.isMyAppointmentsLoading = false;
        state.myAppointments = action?.payload || [];
      })
      .addCase(getMyAppointmentsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isMyAppointmentsLoading = false;
        state.myAppointments = [];
      })
      .addCase(saveGoogleEventsData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(saveGoogleEventsData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(saveGoogleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      })
      .addCase(getGoogleEventsData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(getGoogleEventsData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
        state.googleSyncEvents = Array.isArray(action?.payload)
          ? action.payload
          : [];
      })
      .addCase(getGoogleEventsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
        state.googleSyncEvents = [];
      })
      .addCase(getAllUserWeekAvailabilityData.pending, (state) => {
        state.allUserWeekAvailability = [];
      })
      .addCase(getAllUserWeekAvailabilityData.fulfilled, (state, action) => {
        const payloadData =
          action?.payload?.result !== undefined
            ? action.payload.result
            : action?.payload;
        state.allUserWeekAvailability = Array.isArray(payloadData)
          ? payloadData
          : [];
      })
      .addCase(getAllUserWeekAvailabilityData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.allUserWeekAvailability = [];
      })
      .addCase(addAndUpdateTaskEventData.pending, (state) => {
        state.isEventCalendarLoading = true;
      })
      .addCase(addAndUpdateTaskEventData.fulfilled, (state, action) => {
        state.isEventCalendarLoading = false;
      })
      .addCase(addAndUpdateTaskEventData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isEventCalendarLoading = false;
      });
  },
});
export const {
  setIsEventsLoading,
  setIsEventCalendarLoading,
  setScheduleEventsList,
  setMyAppointmentList,
  setShowBookingSlotsData,
  setBookingSlotsModeData,
  setShowBacktoSlotsButtonData,
  setMyAppointments,
  setGoogleSyncEvents,
} = scheduleEventSlice.actions;
export default scheduleEventSlice.reducer;
